import { motion } from 'framer-motion';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

// Chain icon URLs - Using GitHub avatars and reliable CDNs
const chainIcons = {
  ETH: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/eth.png',
  RBH: '/robinhood-logo.png',
  SOL: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/sol.png',
  ARB: 'https://avatars.githubusercontent.com/u/56302961?s=200&v=4',
  MATIC: 'https://avatars.githubusercontent.com/u/30753617?s=200&v=4',
  OP: 'https://avatars.githubusercontent.com/u/58791460?s=200&v=4',
  BNB: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/bnb.png',
  AVAX: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/avax.png',
  TRX: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/trx.png',
  ZKSYNC: 'https://avatars.githubusercontent.com/u/79418927?s=200&v=4',
  LINEA: 'https://avatars.githubusercontent.com/u/106539330?s=200&v=4',
  MNT: 'https://avatars.githubusercontent.com/u/123416949?s=200&v=4',
  NEAR: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/near.png',
  FTM: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/ftm.png',
  SCROLL: 'https://avatars.githubusercontent.com/u/109401419?s=200&v=4',
  CELO: 'https://avatars.githubusercontent.com/u/37552875?s=200&v=4',
  GNO: 'https://avatars.githubusercontent.com/u/28943927?s=200&v=4',
  SUI: 'https://avatars.githubusercontent.com/u/97aborato5038?s=200&v=4',
  APT: 'https://avatars.githubusercontent.com/u/82895653?s=200&v=4',
  TON: 'https://avatars.githubusercontent.com/u/55018343?s=200&v=4',
  ATOM: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/atom.png',
};

// Chain data - FEWER chains per ring to avoid overlap
const coreChains = [
  { name: 'Ethereum', short: 'ETH' },
  { name: 'Robinhood Network', short: 'RBH' },
  { name: 'Solana', short: 'SOL' },
];

// Ring 1: 4 chains (was 5)
const ring1Chains = [
  { name: 'Arbitrum', short: 'ARB' },
  { name: 'Polygon', short: 'MATIC' },
  { name: 'Optimism', short: 'OP' },
  { name: 'Avalanche', short: 'AVAX' },
];

// Ring 2: 5 chains (was 6)
const ring2Chains = [
  { name: 'BNB Chain', short: 'BNB' },
  { name: 'TRON', short: 'TRX' },
  { name: 'zkSync', short: 'ZKSYNC' },
  { name: 'Near', short: 'NEAR' },
  { name: 'Fantom', short: 'FTM' },
];

// Ring 3: 6 chains (was 7)
const ring3Chains = [
  { name: 'Linea', short: 'LINEA' },
  { name: 'Mantle', short: 'MNT' },
  { name: 'Sui', short: 'SUI' },
  { name: 'Aptos', short: 'APT' },
  { name: 'TON', short: 'TON' },
  { name: 'Cosmos', short: 'ATOM' },
];

// Calculate position on orbit
function getOrbitPosition(angle, radius) {
  const rad = (angle * Math.PI) / 180;
  return {
    x: radius * Math.cos(rad),
    y: radius * Math.sin(rad),
  };
}

// Chain node component
function ChainNode({ chain, angle, radius, size, iconSize }) {
  const [hovered, setHovered] = useState(false);
  const iconUrl = chainIcons[chain.short];
  const pos = getOrbitPosition(angle, radius);

  return (
    <div
      className="absolute transition-transform duration-100 cursor-pointer"
      style={{
        left: '50%',
        top: '50%',
        transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px))`,
        zIndex: hovered ? 50 : 10,
        willChange: 'transform',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="rounded-full flex items-center justify-center overflow-hidden"
        style={{
          width: size,
          height: size,
          background: '#0a0a0a',
          border: `1.5px solid rgba(255,255,255,${hovered ? 0.3 : 0.1})`,
          boxShadow: hovered ? '0 0 20px rgba(255,255,255,0.15)' : 'none',
          transform: hovered ? 'scale(1.12)' : 'scale(1)',
          transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
        }}
      >
        <img
          src={iconUrl}
          alt={chain.name}
          style={{ width: iconSize, height: iconSize, objectFit: 'contain' }}
          loading="lazy"
        />
      </div>
      {hovered && (
        <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 text-[11px] text-zinc-300 whitespace-nowrap font-medium bg-black/95 px-3 py-1.5 rounded-lg border border-white/10 z-50">
          {chain.name}
        </div>
      )}
    </div>
  );
}

// Mobile fallback
function MobileFallback() {
  return (
    <div className="text-center">
      <div className="flex justify-center gap-3 mb-6">
        {coreChains.map((chain, i) => (
          <div
            key={i}
            className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden"
            style={{ background: '#111', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <img src={chainIcons[chain.short]} alt={chain.name} className="w-7 h-7 object-contain" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2 max-w-xs mx-auto">
        {[...ring1Chains, ...ring2Chains, ...ring3Chains].map((chain, i) => (
          <div
            key={i}
            className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden mx-auto"
            style={{ background: '#111', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <img src={chainIcons[chain.short]} alt={chain.name} className="w-5 h-5 object-contain" />
          </div>
        ))}
      </div>
    </div>
  );
}

// SVG Orbit Rings - DASHED, SOLID, DASHED pattern
function OrbitRingsSVG({ ring1Radius, ring2Radius, ring3Radius }) {
  return (
    <svg
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
      viewBox="0 0 1000 1000"
      width="1000"
      height="1000"
      style={{ overflow: 'visible' }}
    >
      {/* Center gradient glow */}
      <defs>
        <radialGradient id="center-glow">
          <stop offset="0%" stopColor="white" stopOpacity="0.1" />
          <stop offset="40%" stopColor="white" stopOpacity="0.03" />
          <stop offset="70%" stopColor="white" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="500" cy="500" r="100" fill="url(#center-glow)" />

      {/* Ring 1 - DASHED - inner */}
      <circle
        cx="500"
        cy="500"
        r={ring1Radius}
        fill="none"
        stroke="rgba(255,255,255,0.2)"
        strokeDasharray="12 8"
        strokeWidth="1.5"
      />

      {/* Ring 2 - SOLID - middle */}
      <circle
        cx="500"
        cy="500"
        r={ring2Radius}
        fill="none"
        stroke="rgba(255,255,255,0.15)"
        strokeWidth="1"
      />

      {/* Ring 3 - DASHED - outer */}
      <circle
        cx="500"
        cy="500"
        r={ring3Radius}
        fill="none"
        stroke="rgba(255,255,255,0.1)"
        strokeDasharray="8 12"
        strokeWidth="1"
      />
    </svg>
  );
}

export default function ChainOrbit() {
  const [angles, setAngles] = useState({ r1: 0, r2: 0, r3: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(performance.now());

  // LARGER radii to spread chains more
  const ring1Radius = 160;
  const ring2Radius = 300;
  const ring3Radius = 450;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Ultra-smooth animation using requestAnimationFrame
  const animate = useCallback((currentTime) => {
    const delta = (currentTime - lastTimeRef.current) / 1000;
    lastTimeRef.current = currentTime;

    setAngles((prev) => ({
      r1: (prev.r1 + 3 * delta) % 360,    // Very slow: ~120s per rotation
      r2: (prev.r2 - 2 * delta) % 360,    // Slower: ~180s, counter-clockwise
      r3: (prev.r3 + 1.5 * delta) % 360,  // Slowest: ~240s
    }));

    animationRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    if (isMobile) return;

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isMobile, animate]);

  // Pre-calculate base angles for even distribution
  const ring1BaseAngles = useMemo(() => 
    ring1Chains.map((_, i) => (360 / ring1Chains.length) * i - 90), 
  []);
  
  const ring2BaseAngles = useMemo(() => 
    ring2Chains.map((_, i) => (360 / ring2Chains.length) * i - 90 + 36), 
  []);
  
  const ring3BaseAngles = useMemo(() => 
    ring3Chains.map((_, i) => (360 / ring3Chains.length) * i - 90 + 30), 
  []);

  return (
    <section
      id="chains"
      className="py-[120px] bg-black"
      data-testid="chain-orbit-section"
    >
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-[32px] font-bold tracking-[-0.02em] text-white mb-3"
        >
          20+ chains. One API.
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-[15px] text-zinc-500 mb-16"
        >
          Every chain is a plugin. Activate what you need.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative flex justify-center"
        >
          {isMobile ? (
            <MobileFallback />
          ) : (
            <div
              className="relative"
              style={{ width: 1000, height: 1000 }}
              data-testid="orbit-container"
            >
              {/* SVG rings */}
              <OrbitRingsSVG 
                ring1Radius={ring1Radius} 
                ring2Radius={ring2Radius} 
                ring3Radius={ring3Radius} 
              />

              {/* Center cluster */}
              <div
                className="absolute flex gap-3"
                style={{
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 40,
                }}
              >
                {coreChains.map((chain, i) => (
                  <div
                    key={i}
                    className="w-16 h-16 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 hover:scale-110 overflow-hidden"
                    style={{
                      background: '#0a0a0a',
                      border: '2px solid rgba(255,255,255,0.2)',
                      boxShadow: '0 0 40px rgba(255,255,255,0.08)',
                    }}
                  >
                    <img
                      src={chainIcons[chain.short]}
                      alt={chain.name}
                      className="w-9 h-9 object-contain"
                    />
                  </div>
                ))}
              </div>

              {/* Ring 1 - 4 chains */}
              {ring1Chains.map((chain, i) => (
                <ChainNode
                  key={chain.short}
                  chain={chain}
                  angle={ring1BaseAngles[i] + angles.r1}
                  radius={ring1Radius}
                  size={52}
                  iconSize={30}
                />
              ))}

              {/* Ring 2 - 5 chains */}
              {ring2Chains.map((chain, i) => (
                <ChainNode
                  key={chain.short}
                  chain={chain}
                  angle={ring2BaseAngles[i] + angles.r2}
                  radius={ring2Radius}
                  size={46}
                  iconSize={26}
                />
              ))}

              {/* Ring 3 - 6 chains */}
              {ring3Chains.map((chain, i) => (
                <ChainNode
                  key={chain.short}
                  chain={chain}
                  angle={ring3BaseAngles[i] + angles.r3}
                  radius={ring3Radius}
                  size={42}
                  iconSize={24}
                />
              ))}
            </div>
          )}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-[13px] text-zinc-600 mt-12"
        >
          + more chains added monthly
        </motion.p>
      </div>
    </section>
  );
}
